import { runInAction, observable, computed, action } from 'mobx'
import { makeObservableRowBase, RowBase } from 'shared/ui/TableNew/model/RowBase'
import { logger } from 'shared/lib'
import { RequestCancelAxios } from 'shared/api/lib'
import { variantActionsProps } from 'shared/ui'
import { type BroadcastRecurring, BroadcastRecurringChild } from 'entities/Broadcast'
import { BroadcastApi } from 'entities/Broadcast/api/broadcast'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import type { IResponseOneTimeBroadcast } from 'entities/Broadcast/api/types'
import { RowBroadcastOneTime } from 'entities/Broadcast/model/Table/RowBroadcastOneTime'
import { getBroadcastActions } from 'pages/broadcasts/ui/BroadcastActions'

export class RowBroadcastRecurring extends RowBase<BroadcastRecurring, RowBroadcastOneTime> {
  private _childrenMap: Map<number, BroadcastRecurringChild> = new Map()
  private _page = 1
  private _is_last_page = true
  private _cancelRequest = new RequestCancelAxios()

  constructor(
    origin: BroadcastRecurring,
    private _entities: IEntitiesStore,
    private _fromDetailPage?: boolean
  ) {
    super(origin)

    this.setIsCanExpanded(true)

    if (this._fromDetailPage) {
      this.onLoadExpandedData()
    }

    makeObservableRowBase(this, {
      _page: observable,
      _is_last_page: observable,
      _childrenMap: observable,
      rows: computed,
      isMoreExpand: computed,
      onLoadMore: action,
      onDelete: action,
    })
  }

  get name() {
    return this.origin.name
  }

  get type() {
    return this.origin.type
  }

  get user() {
    return this.origin.user
  }

  get status() {
    return this.origin.status
  }

  get isInvalid() {
    return this.origin.isInvalid
  }

  get rows() {
    return Array.from(this._childrenMap.values()).map((item) => {
      const row = new RowBroadcastOneTime(item)

      row.setActionsProps({
        ...variantActionsProps.table,
        actions: getBroadcastActions({
          broadcast: row.origin,
          onSuccessDelete: () => this.onDelete([row.id]),
        }),
      })

      return row
    })
  }

  get isMoreExpand() {
    return !this._is_last_page
  }

  toggleExpand = async () => {
    this._childrenMap.clear()
    this._is_last_page = true
    this._page = 1
    super.toggleExpanded()
    this._cancelRequest.reset()

    try {
      if (this.expanded) {
        await this.onLoadExpandedData()
      }
    } catch (e) {
      logger.error(e)
    }
  }

  private updateChildren = (response?: IResponseOneTimeBroadcast) => {
    if (response) {
      const children = this._childrenMap.get(response.id)
      if (children) {
        children.syncOrigin(response)
      }
    } else if (this._fromDetailPage || this.expanded) {
      this.setLoading(true)
      this._is_last_page = true
      this._page = 1
      this.onLoadExpandedData(true)
    }
  }

  onLoadExpandedData = async (fromUpdate?: boolean) => {
    try {
      this._cancelRequest.init()

      runInAction(() => {
        if (!this.isMoreExpand) this.setLoading(true)
      })

      const { data } = await BroadcastApi.getRecurringBroadcasts(
        this.id,
        {
          page: this._page,
        },
        {
          cancelToken: this._cancelRequest.token,
        }
      )
      runInAction(() => {
        if (fromUpdate) {
          this._childrenMap = new Map(
            data.data.map((item) => [
              item.id,
              new BroadcastRecurringChild({
                item: item,
                parentId: this.id,
                entities: this._entities,
              }),
            ])
          )
        } else {
          data.data.forEach((item) => {
            this._childrenMap.set(
              item.id,
              new BroadcastRecurringChild({
                item: item,
                parentId: this.id,
                entities: this._entities,
              })
            )
          })
        }

        this._is_last_page = data.meta.is_last_page
      })
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this.setLoading(false)
      })
    }
  }

  onLoadMore = async () => {
    try {
      this._page = this._page + 1

      runInAction(() => {
        this.setLoadingMore(true)
      })
      await this.onLoadExpandedData()
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this.setLoadingMore(false)
      })
    }
  }

  onDelete = (ids: number[]) => {
    ids.forEach((id) => {
      this._childrenMap.delete(id)
    })
  }
}
